<script setup lang="ts">
import { ref, unref, useTemplateRef } from "vue";
import { Accordion } from "~/components/clarity-ui/Accordion";
import { AccordionDetails } from "~/components/clarity-ui/Accordion/AccordionDetails";
import { AccordionSummary } from "~/components/clarity-ui/Accordion/AccordionSummary";
import LinkComponent from "~/templates/components/cms/LinkComponent.vue";
import LevelTwo from "~/templates/partials/navigation/LevelTwo.vue";
import { ProductTeaser } from "~/templates/partials/navigation/ProductTeaser";
import { mapNodes, filterNodesByType } from "~/utils/helper/magnolia";

export interface MainNavigationProps {
  header: object;
}

const { header } = defineProps<MainNavigationProps>();
const emit = defineEmits(["submenu-toggle"]);

const entries = computed(() => {
  return filterNodesByType(mapNodes(header), "nav:");
});
const { isTabletOrMobile } = useDevice();

const { activeMenu, activeMenuIndex, isAccordionOpen, isMobileMode, isMobileOpen, resetState } =
  useMainNavigationState();
const nav = ref(null);
const list = ref(null);
const listItems = ref(null);
const accordions = useTemplateRef("accordions");
const submenuRefs = ref(null);

const isMounted = ref(false);

const close = () => {
  if (activeMenu.value) {
    activeMenu.value = null;
    activeMenuIndex.value = null;
    emit("submenu-toggle");
  }
};

const toggleSubMenu = (event) => {
  const { index } = event.currentTarget.dataset;
  const submenu = submenuRefs.value.find((menu) => menu.$el.dataset.index === index);
  const oldActiveIndex = activeMenu.value?.$el.dataset.index ?? -1;
  const closeActive = index === oldActiveIndex;
  activeMenu.value = closeActive ? null : submenu;
  activeMenuIndex.value = activeMenu.value ? parseInt(index) : null;

  emit("submenu-toggle");
};

const evaluateMobile = () => {
  isMobileMode.value = unref(isTabletOrMobile);

  if (isMobileMode.value) {
    close();
  }
  return isMobileMode.value;
};

const onAccordionToggle = (event) => {
  isAccordionOpen.value = accordions.value?.some((accordion) => accordion.open);

  // scroll back to top
  if (event.target.open) {
    unref(list)?.scrollTo({ behavior: "smooth", top: 0 });
  }
};

const setMobileOpen = (value) => {
  isMobileOpen.value = value;
};

onMounted(() => {
  isMounted.value = true;
  resetState();

  watch(
    [isTabletOrMobile],
    () => {
      setMobileOpen(false);
      evaluateMobile();
    },
    { immediate: true },
  );
});

defineExpose({ activeMenu, close, evaluateMobile, setMobileOpen });
</script>

<template>
  <nav
    ref="nav"
    class="main-navigation w-full xl:px-16"
    :class="{ hidden: !isMounted, 'xl:block': !isMounted }"
  >
    <template v-if="!isMobileMode">
      <ul ref="list" class="flex content-center items-center gap-7 motion-safe:transition-all">
        <li v-for="(entry, index) in entries" :key="entry['@id']" ref="listItems">
          <LinkComponent
            v-if="entry['@nodeType'] === 'nav:link'"
            class="h-11 cursor-pointer whitespace-nowrap font-medium hover:text-deep-blue motion-safe:transition-colors"
            :link="entry.link"
            >{{ entry.link?.label }}</LinkComponent
          >
          <template v-else>
            <button
              class="h-11 cursor-pointer whitespace-nowrap font-medium hover:text-deep-blue motion-safe:transition-colors"
              :class="{ 'text-deep-blue': index === activeMenuIndex }"
              :data-index="index"
              @click="toggleSubMenu"
            >
              {{ entry.title }}
            </button>
            <LevelTwo
              v-if="entry['@nodeType'] === 'nav:main'"
              ref="submenuRefs"
              class="container absolute left-0 right-0 top-full"
              :class="{ invisible: !isMobileMode && index !== activeMenuIndex }"
              :is-active="index === activeMenuIndex"
              :data-index="index"
              :level="entry"
              @level-close="close"
            />
          </template>
        </li>
      </ul>
    </template>
    <template v-else>
      <ul
        ref="list"
        class="h-4 content-center items-center px-4 text-black motion-safe:transition-all md:px-8"
        :class="{
          hidden: !isMobileOpen,
          'is-mobile-open absolute inset-0 flex h-screen scroll-p-24 flex-col items-start overflow-auto pb-32 opacity-100':
            isMobileOpen,
        }"
      >
        <Accordion>
          <li v-for="(entry, index) in entries" :key="entry['@id']" ref="listItems" class="w-full">
            <LinkComponent
              v-if="entry['@nodeType'] === 'nav:link'"
              class="my-2 block cursor-pointer truncate whitespace-nowrap py-1.5 text-xl font-medium hover:text-deep-blue motion-safe:transition-colors xl:text-2xl"
              :link="entry.link"
              >{{ entry.link?.label }}</LinkComponent
            >
            <AccordionDetails
              v-else
              :id="`nav-${index}`"
              ref="accordions"
              @toggle="onAccordionToggle"
            >
              <AccordionSummary
                :class="{ 'text-deep-blue': index === activeMenuIndex }"
                :data-index="index"
                @click="toggleSubMenu"
              >
                {{ entry.title }}
              </AccordionSummary>
              <LevelTwo
                v-if="entry['@nodeType'] === 'nav:main'"
                ref="submenuRefs"
                :is-active="index === activeMenuIndex"
                :data-index="index"
                :level="entry"
                @level-close="close"
              />
            </AccordionDetails>
          </li>
        </Accordion>
        <li v-if="isMobileOpen && !isAccordionOpen && header.teaserHeadline" class="mt-3.5">
          <ProductTeaser
            :heading="header.teaserHeadline"
            :subline="header.teaserSubline"
            :link="header.teaserLink"
            :image="header.image"
          />
        </li>
      </ul>
    </template>
  </nav>
</template>

<style scoped>
.is-mobile-open {
  --top-offset: theme("spacing.16");
  --bottom-offset: theme("spacing.4");

  top: var(--top-offset);
  height: calc(100vh - var(--top-offset) - var(--bottom-offset));
}
</style>
